import {DateTime} from "luxon";
import {useState} from "react";
import {sortImmutable} from "../../../../../lib/utils.ts";
import {ArrowLeft, ArrowRight} from "lucide-react";
import {Link} from "react-router-dom";
import {TabSwitcher} from "../../../../../components/general/TabSwitcher.tsx";
import LoadingFrame from "../../../../../components/LoadingFrame.tsx";
import {MoneyLabel} from "../../../../../components/general/MoneyLabel.tsx";
import {Growth} from "../../Stats.tsx";
import MonthOverviewPieChart from "./MonthOverviewPieChart.tsx";
import { FilterOperation } from "../TransactionsPage/FilterService.ts";
import AIToolTip from "../../../../../components/general/AIToolTip.tsx";
import { GLAWithIcon } from "../../../../../components/general/GLAWithIcon.tsx";


export interface MonthOverviewAllocation {
    name: string;
    total: number;
    icon: string | null;
}

export interface MonthOverviewDataSpec {
    previous_month_inflow: number,
    previous_month_outflow: number,
    current_month_inflow: number,
    current_month_outflow: number,
    inflow_allocations: MonthOverviewAllocation[],
    outflow_allocations: MonthOverviewAllocation[],
}

interface MonthOverviewProps {
    data: MonthOverviewDataSpec,
    currentMonth: Date,
    monthlySummary: string,
    flow: 'outflow' | 'inflow',
    isLoading: boolean,
    onDateChange: (change: 1 | -1) => void,
    onFlowChange: (newFlow: 'outflow' | 'inflow') => void

}

type AllocationListItem = {
    displayName: string;
    total: number;
    icon: string | null;
};

function MonthSummary({summaryBody}: {summaryBody: string}) {

    return <>
        <div className="flex items-top bg-gray-50 p-4 px-6 rounded-md gap-4">
            <div>
                <div className="inline-flex items-center gap-2  mb-2">
                    <h2 className="text-lg text-gray-900">Maandelijkse samenvatting</h2>
                    <AIToolTip text={'This summary is generated by Transaction Intelligence'}/>

                </div>
                <p className="text-gray-600 text-sm mb-1">
                    {summaryBody}
                </p>
            </div>
        </div>
    </>
}

export function MonthOverview({data, monthlySummary, currentMonth, flow, isLoading, onDateChange, onFlowChange}: MonthOverviewProps) {
    const previousMonth = DateTime.fromISO(currentMonth.toISOString()).minus({months: 1}).toJSDate()
    const [hoveredCategory, setHoveredCategory] = useState<string | null>(null)

    function selectForFlow<T>(outflow: T, inflow: T): T {
        if (flow === 'outflow')
            return outflow
        return inflow
    }

    const allocations: AllocationListItem[] = data
        ? sortImmutable<AllocationListItem>(
            selectForFlow(data.outflow_allocations, data.inflow_allocations).map((allocation) => ({
                displayName: allocation.name,
                total: allocation.total,
                icon: allocation.icon,
            })),
            (a, b) => b.total - a.total,
        )
        : [];

    return <>

        <div className="flex items-center gap-4">

            <div className="flex items-center gap-2">
                <button className="bg-gray-100 p-2 rounded-full"
                        onClick={() => {
                            onDateChange(-1)
                        }}>
                    <ArrowLeft className="size-3"/>
                </button>

                <button className="bg-gray-100 p-2 rounded-full"
                        onClick={() => {
                            onDateChange(1)
                        }}>
                    <ArrowRight className="size-3"/>
                </button>
            </div>
            <h2>{currentMonth.toLocaleString('nl-NL', {month: 'long'})} {currentMonth.getFullYear()} </h2>

            <div className={'ml-auto'}>
                <TabSwitcher key={flow} tabs={[
                    {
                        id: 'inflow',
                        label: 'Inkomsten'
                    },
                    {
                        id: 'outflow',
                        label: 'Uitgaven'
                    },
                ]
                } onTabChange={(id: string) => {
                    onFlowChange(id as 'outflow' | 'inflow')
                }}
                             defaultId={flow}
                />
            </div>
        </div>
        <LoadingFrame isLoading={isLoading}>

            <div className="w-full my-4">
                <div className="flex flex-col flex-start text-left py-4 px-6 bg-gray-50 rounded-lg transition-all">
                    <div
                        className="xl:text-lg leading-6 font-medium text-gray-700 flex mt-2 items-center w-full justify-between">
                        <span className={'block'}>{selectForFlow('Uitgaven', 'Inkomsten')} </span>
                        <span className='block text-sm text-gray-500 font-normal'>
                                    {currentMonth.toLocaleString('nl-NL', {month: 'long'})} vs {previousMonth.toLocaleString('nl-NL', {month: 'long'})}
                                </span>
                    </div>
                    <dd className="order-first text-xl fond-bold 2xl:text-4xl font-bold tracking-tight text-gray-900 flex justify-between">
                            <span className={"text-3xl"}>
                                <MoneyLabel
                                    value={selectForFlow(
                                        data?.current_month_outflow / 100,
                                        data?.current_month_inflow / 100
                                    )}/>
                            </span>

                        <Growth
                            value={Math.round(
                                selectForFlow(
                                    data?.current_month_outflow / data?.previous_month_outflow - 1,
                                    data?.current_month_inflow / data?.previous_month_inflow - 1) * 100
                            )}
                            goodWhenLow={selectForFlow(true, false)}/>
                    </dd>
                </div>
            </div>
            <MonthSummary summaryBody={monthlySummary}/>
            <div className="mt-8">
                <MonthOverviewPieChart data={data} flow={flow} currentMonth={currentMonth} hoveredCategory={hoveredCategory} />
                <ul>
                    {allocations.map((allocation => {
                        const date = new Date(currentMonth);
                        const startDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
                        const endDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()}`;

                        const categoryLabel = allocation.displayName;

                        const filters = [
                            {
                                id: 'filterFlow',
                                operation: FilterOperation.EQUALS,
                                columnId: 'flow',
                                values: [flow]
                            },
                            {
                                id: 'filterCategory',
                                operation: FilterOperation.EQUALS,
                                columnId: 'category',
                                values: [categoryLabel]
                            },
                            {
                                id: 'filterBookingDate',
                                operation: FilterOperation.BETWEEN,
                                columnId: 'bookingDate',
                                values: [startDate, endDate]
                            }
                        ];

                        return (
                            <Link
                                key={allocation.displayName}
                                to={`/transactions?filters=${encodeURIComponent(JSON.stringify(filters))}`}
                                onMouseEnter={() => setHoveredCategory(allocation.displayName)}
                                onMouseLeave={() => setHoveredCategory(null)}
                                className={`flex w-full items-start justify-between gap-3 rounded-lg px-4 py-3 transition-colors hover:bg-gray-50 ${hoveredCategory === allocation.displayName ? 'bg-gray-50' : ''}`}
                            >
                                <GLAWithIcon icon={allocation.icon} label={categoryLabel} />
                                <span className="ml-auto">€ <MoneyLabel value={allocation.total / 100}/></span>
                            </Link>
                        );
                    }))}
                </ul>
            </div>

        </LoadingFrame>


    </>
}
